import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { QaPage, QaPlace } from "@CommonApi/merchflows/_merchflowId_/qa";
import { Color, Flex, Scroller, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { Composer, ComposerInput, SendButton } from "../ConfigCopilotPanel";
import { qaPlaceUrlOf } from "../store/qaPlaces";
import { useOQa, type OQaEntry } from "../store/useOQa";
import { OAnswerFeedback } from "./OAnswerFeedback";
import { LinkButton, OButton, OMark, OSays, SuggestionChip, UserSays } from "./OParts";

interface Props {
  merchflowId: number;
  /** Where the panel is: every question carries it. */
  page: QaPage;
  /** A place was opened: the panel makes room for it. */
  onLeave?: () => void;
}

const OPENING = [
  "configCopilot.qa.good",
  "configCopilot.qa.bad",
  "configCopilot.qa.better",
] as const;
const OPENING_ON_STORES = [
  "configCopilot.qa.storesAttention",
  "configCopilot.qa.storesNext",
] as const;
const OPENING_ON_STORE_POG = ["configCopilot.qa.whyScore", "configCopilot.qa.missing"] as const;
const OPENING_ON_BASE_POG = ["configCopilot.qa.cdtReady"] as const;

const openingKeysOf = (page: QaPage) => [
  ...(page.kind === "stores"
    ? ([
        page.storePogTriggerId === undefined
          ? "configCopilot.qa.storesResults"
          : "configCopilot.qa.storesRun",
        ...OPENING_ON_STORES,
      ] as const)
    : OPENING),
  ...(page.kind === "store_pog" ? OPENING_ON_STORE_POG : []),
  ...(page.kind === "base_pog" || page.kind === "cdt_studio" ? OPENING_ON_BASE_POG : []),
];

/** O's merchflow Q&A: read-only answers, links to where to act, follow-ups as words. */
export const OAskPanel = ({ merchflowId, page, onLeave }: Props) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const o = useOQa(merchflowId);
  const held = o.retry ?? o.failed;
  const [draft, setDraft] = useState(held?.text ?? "");
  const input = useRef<HTMLInputElement>(null);
  const thread = useRef<HTMLDivElement>(null);

  // A question that fails after the drawer opened again comes back here, unless words were typed since.
  useEffect(() => {
    if (held) setDraft((now) => now || held.text);
  }, [held]);

  // Focus and scroll stay inside the drawer: on a page wider than the window, the browser
  // would scroll the whole window sideways to reveal the box and push the drawer off screen.
  useEffect(() => {
    input.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const at = thread.current;
    if (at && o.entries.length > 0) at.scrollTo?.({ top: at.scrollHeight, behavior: "smooth" });
  }, [o.entries.length]);

  const say = async (text: string, askedOn: QaPage = page) => {
    if (o.isAsking || o.rateLimitSeconds > 0) return;
    const isDone = await o.ask(text, askedOn);
    if (!isDone) setDraft((now) => now || text);
  };

  const send = () => {
    const text = draft.trim();
    if (!text || o.isAsking || o.rateLimitSeconds > 0) return;
    setDraft("");
    input.current?.focus({ preventScroll: true });
    void say(text);
  };

  const go = (place: QaPlace) => {
    const url = qaPlaceUrlOf(merchflowId, place);
    if (url === null) return;
    onLeave?.();
    navigate(url);
  };

  const chipsOf = (chips: readonly string[]) => (
    <Flex gap="6px" flexWrap="wrap" data-testid="o-ask-chips">
      {chips.map((chip) => (
        <SuggestionChip
          key={chip}
          type="button"
          disabled={o.isAsking || o.rateLimitSeconds > 0}
          onClick={() => void say(chip)}
        >
          {chip}
        </SuggestionChip>
      ))}
    </Flex>
  );

  const lastReply = o.entries.findLast((entry) => entry.role === "o" && !entry.isLocal);

  const reply = (entry: OQaEntry) => (
    <Flex key={entry.id} column gap="8px" data-testid="o-ask-reply">
      <OSays size={22}>{entry.text}</OSays>
      {!entry.isLocal && (
        <Flex column gap="8px" padding="0 0 0 32px">
          {entry.goTo && entry.goTo.length > 0 && (
            <Flex gap="8px" flexWrap="wrap" align="center">
              {entry.goTo.map((place, index) =>
                place.kind === "data" ? (
                  <Flex key={index} data-testid="o-ask-data">
                    <Text variant="small1" color={Color.textSecondary}>
                      {place.label}
                    </Text>
                  </Flex>
                ) : (
                  <OButton
                    key={index}
                    type="button"
                    isPrimary={index === 0}
                    data-testid="o-ask-go"
                    onClick={() => go(place)}
                  >
                    {place.label}
                  </OButton>
                ),
              )}
            </Flex>
          )}
          <OAnswerFeedback
            merchflowId={merchflowId}
            entry={entry}
            onVoting={o.markVoting}
            onVoted={o.markVoted}
            onVoteFailed={o.markVoteFailed}
          />
          {entry === lastReply && entry.chips && entry.chips.length > 0 && chipsOf(entry.chips)}
        </Flex>
      )}
    </Flex>
  );

  const opening = openingKeysOf(page).map((key) => t(key));
  const isRunning = o.running === true;

  return (
    <Flex column flexGrow minHeight="0" data-testid="o-ask">
      <Scroller ref={thread} gutter="stable">
        <Flex column gap="12px" padding="14px 16px" aria-live="polite" data-testid="o-ask-thread">
          {o.entries.length === 0 && <OSays size={22}>{t("configCopilot.qa.hello")}</OSays>}
          {o.entries.map((entry) =>
            entry.role === "user" ? <UserSays key={entry.id}>{entry.text}</UserSays> : reply(entry),
          )}
        </Flex>
      </Scroller>

      <Flex column gap="8px" padding="10px 16px">
        {o.isAsking && (
          <Flex gap="10px" align="center" data-testid="o-ask-thinking">
            <OMark size={22} />
            <Text variant="body2" color={Color.textSecondary}>
              {t("configCopilot.qa.thinking")}
            </Text>
          </Flex>
        )}

        {isRunning && !o.isAsking && (
          <Flex data-testid="o-ask-running">
            <Text variant="small1" color={Color.textSecondary}>
              {t("configCopilot.qa.running")}
            </Text>
          </Flex>
        )}

        {o.trouble && (
          <Flex gap="10px" align="center" data-testid="o-ask-trouble">
            <Text variant="body2" color={Color.alertHighText}>
              {o.trouble.line}
            </Text>
            {o.trouble.retry && (
              <LinkButton type="button" onClick={o.trouble.retry}>
                {t("configCopilot.retry")}
              </LinkButton>
            )}
          </Flex>
        )}

        {(o.rateLimitSeconds > 0 || o.retry) && (
          <Flex gap="10px" align="center" data-testid="o-ask-rate-limit">
            <Text variant="body2" color={Color.alertHighText}>
              {t(
                o.rateLimitSeconds > 0
                  ? "configCopilot.qa.rateLimited"
                  : "configCopilot.qa.rateLimitReady",
                { count: o.rateLimitSeconds },
              )}
            </Text>
            {o.retry && (
              <LinkButton
                type="button"
                disabled={o.isAsking || o.rateLimitSeconds > 0}
                onClick={() => {
                  const retry = o.retry;
                  if (!retry) return;
                  setDraft("");
                  void say(retry.text, retry.page);
                }}
              >
                {t("configCopilot.retry")}
              </LinkButton>
            )}
          </Flex>
        )}

        {(o.entries.length === 0 || o.isUnavailable) && chipsOf(opening)}

        <Composer
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          <ComposerInput
            ref={input}
            value={draft}
            aria-label={t("configCopilot.askO")}
            placeholder={t(
              isRunning ? "configCopilot.qa.placeholderRunning" : "configCopilot.qa.placeholder",
            )}
            data-testid="o-ask-input"
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <SendButton
            type="submit"
            aria-label={t("configCopilot.send")}
            disabled={o.isAsking || o.rateLimitSeconds > 0 || !draft.trim()}
          >
            ↑
          </SendButton>
        </Composer>
      </Flex>
    </Flex>
  );
};
